import { useState } from 'react'
import { Pencil, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '../Button/Button'
import { BitsPopover, Grip, ToolbarButton, type DragOffset } from './WorkbenchFloatingBits'
import { SwatchGrid } from './WorkbenchColorPicker'
import { EntryCircleIcon, StopCrossIcon, TakeProfitDiamondIcon } from './WorkbenchDrawIcons'
import { IDEA_STEPS, type IdeaRole, markLabel } from './tradeIdeaVocab'
import { isIdeaComplete, type TradeIdea } from './tradeIdeas'

/**
 * The bar that says which trade idea you are building and how far along it is.
 *
 * Standard toolbar chrome — popover surface, border, shadow — deliberately NOT
 * the idea's colour: a coloured frame around a toolbar reads as an alert. The
 * hue appears only on the role shapes and the track that joins them, which is
 * where it means "this idea", and on the chart itself.
 */

const ROLE_ICON: Record<IdeaRole, typeof EntryCircleIcon> = {
  entry: EntryCircleIcon,
  sl: StopCrossIcon,
  tp: TakeProfitDiamondIcon,
}

export interface WorkbenchTradeIdeaStripProps {
  idea: TradeIdea
  /** The step the next chart click will fill. */
  currentStep: IdeaRole | null
  onPickStep: (step: IdeaRole) => void
  onPickColor: (color: string) => void
  onDelete: () => void
  onExit: () => void
  /** Sits lower when the Canvas/Walkthrough pill is on screen. */
  belowPill?: boolean
  /**
   * Width of chrome docked over the pane's left edge — the trade-idea panel, or
   * 0 when it is shut.
   *
   * The strip centres on the chart the trader can SEE, not on the pane. Centred
   * on the pane it reached back across the open panel and, being the higher
   * layer, took the hover and the clicks meant for the level rows beneath it.
   */
  clearLeft?: number
}

export function WorkbenchTradeIdeaStrip({
  idea,
  currentStep,
  onPickStep,
  onPickColor,
  onDelete,
  onExit,
  belowPill = false,
  clearLeft = 0,
}: WorkbenchTradeIdeaStripProps) {
  const [drag, setDrag] = useState<DragOffset>({ dx: 0, dy: 0 })
  const [colorOpen, setColorOpen] = useState(false)
  const complete = isIdeaComplete(idea)

  return (
    <div
      data-idea-strip
      className={cn(
        'absolute left-1/2 z-[70] -translate-x-1/2',
        belowPill ? 'top-[5.25rem]' : 'top-12',
      )}
      // Half the docked width moves the centre off the panel and onto what is
      // left of the chart. The drag offset lives on the inner box, so this is
      // free to be a margin and the two never fight.
      style={{ marginLeft: clearLeft / 2 }}
    >
      <div
        className="w-[26rem] max-w-[92vw] rounded-md border border-border bg-popover shadow-popover"
        style={{ transform: `translate(${drag.dx}px, ${drag.dy}px)` }}
      >
        <div className="flex items-center gap-1 border-b border-border px-1 py-1">
          <Grip drag={drag} setDrag={setDrag} />
          <span className="truncate text-label text-foreground">{idea.name}</span>
          <span className="shrink-0 text-caption text-muted-foreground">· edit mode</span>

          <div className="ml-auto flex items-center gap-0.5">
            <BitsPopover
              open={colorOpen}
              onOpenChange={setColorOpen}
              align="right"
              anchor={
                <ToolbarButton
                  title="Trade idea colour"
                  active={colorOpen}
                  onClick={() => setColorOpen((v) => !v)}
                >
                  <span className="flex flex-col items-center gap-0.5">
                    <Pencil size={13} strokeWidth={1.75} />
                    <span className="h-[3px] w-4 rounded-full" style={{ background: idea.color }} />
                  </span>
                </ToolbarButton>
              }
            >
              {/* The full grid, minus the hues an idea may not wear — its
                  colour is an identity, and identities must not collide with
                  the colours that already mean buy, sell or "just a drawing".
                  Closes on the pick: with seventy swatches up, a popover left
                  open covers the chart the choice was made for. */}
              <SwatchGrid
                cur={idea.color}
                scope="tradeIdea"
                onPick={(c) => {
                  onPickColor(c)
                  setColorOpen(false)
                }}
              />
            </BitsPopover>

            <ToolbarButton title="Delete trade idea" onClick={onDelete}>
              <Trash2 size={13} strokeWidth={1.75} />
            </ToolbarButton>

            {complete ? (
              <Button
                type="button"
                size="sm"
                onClick={onExit}
                title="Save this trade idea and leave edit mode"
                className="mx-1 h-6 min-h-6 px-2.5 text-caption"
              >
                Save
              </Button>
            ) : (
              <button
                type="button"
                onClick={onExit}
                title="Leave edit mode — what you have marked is kept"
                className="mx-1 rounded px-1.5 py-1 text-caption text-muted-foreground hover:text-foreground cursor-pointer"
              >
                Save
              </button>
            )}

            {/* Window chrome, in the corner every other window in the app puts
                it, and the one control that is here in both states — which is
                what makes it learnable. Leaving IS saving: the marks are already
                on the chart, so this, Escape and Save are the same act said at
                three volumes. The name teaches the key. */}
            <ToolbarButton title="Close (Esc) — what you have marked is kept" onClick={onExit}>
              <X size={13} strokeWidth={1.75} />
            </ToolbarButton>
          </div>
        </div>

        {/* Progress track. Connectors are elastic so the row stays centred at
            any width, and fill in as each step lands. */}
        <div className="flex items-start px-3 py-2">
          {IDEA_STEPS.map((step, i) => {
            const marks = idea.marks.filter((m) => m.role === step.key)
            const done = marks.length > 0
            const doing = currentStep === step.key
            const Icon = ROLE_ICON[step.key]
            // Entry and stop are one-per-idea, so once placed there is nothing
            // to go back for. Take profits never fill up.
            const spent = done && !step.unlimited
            const label =
              step.key === 'entry'
                ? markLabel('entry', 'entry', idea.dir)
                : step.unlimited && marks.length > 1
                  ? `${step.label} ×${marks.length}`
                  : step.label
            return (
              <div key={step.key} className="flex flex-1 items-start last:flex-none">
                <button
                  type="button"
                  disabled={spent}
                  aria-current={doing ? 'step' : undefined}
                  onClick={() => onPickStep(step.key)}
                  title={
                    spent
                      ? `${label} set at ${marks[0].value.toFixed(marks[0].precision)}`
                      : `Mark your ${label.toLowerCase()}`
                  }
                  className={cn(
                    'flex shrink-0 items-start gap-1.5',
                    spent ? 'cursor-default' : 'cursor-pointer',
                  )}
                >
                  <span
                    className="mt-px flex h-3.5 w-3.5 items-center justify-center rounded-full"
                    style={{
                      background: done
                        ? `color-mix(in srgb, ${idea.color} 20%, transparent)`
                        : 'transparent',
                    }}
                  >
                    <Icon
                      size={10}
                      strokeWidth={done ? 1.75 : 1.4}
                      className={done || doing ? undefined : 'text-muted-foreground'}
                      {...(done || doing ? { style: { color: idea.color } } : {})}
                    />
                  </span>
                  <span
                    className={cn(
                      'whitespace-nowrap text-caption',
                      doing ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {label}
                  </span>
                </button>
                {i < IDEA_STEPS.length - 1 && (
                  <span
                    aria-hidden
                    className="mt-[7px] h-px flex-1 self-start ml-1.5 mr-1.5"
                    style={{
                      background: done
                        ? idea.color
                        : 'color-mix(in srgb, var(--muted-foreground) 35%, transparent)',
                    }}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
